import SearchableSelect from '../components/search/SearchableSelect';
import React, { useState, useEffect } from 'react';
import {
  Typography,
  Container,
  Box,
  Button,
  CircularProgress,
  Grid,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  useTheme,
  Stack,
  Paper,
  Chip,
  Tabs,
  Tab
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SyncIcon from '@mui/icons-material/Sync';
import TransactionTable from '../components/TransactionTable';
import AccountCard from '../components/AccountCard';
import AddTransactionModal from '../components/AddTransactionModal';
import TransferModal from '../components/TransferModal';
import AddAccountModal from '../components/AddAccountModal';
import EditTransactionModal from '../components/EditTransactionModal';
import AccountMigrationModal from '../components/AccountMigrationModal';
import ComplianceDashboardAlert from '../components/compliance/ComplianceDashboardAlert';
import { getAccounts, getTransactions, getCategories, deleteTransaction, getAccountsSummary, getTransactionDateRange, getArchivedAccounts, reactivateAccount, triggerTellerSync } from '../services/api';

function DashboardPage() {
  const [accounts, setAccounts] = useState([]);
  const [archivedAccounts, setArchivedAccounts] = useState([]);
  const [showArchivedAccounts, setShowArchivedAccounts] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [accountsSummary, setAccountsSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successNotice, setSuccessNotice] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('last_30_days');
  const [monthOptions, setMonthOptions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [isMigrationModalOpen, setIsMigrationModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileFilterExpanded, setMobileFilterExpanded] = useState(false);
  const [accountsGroupExpanded, setAccountsGroupExpanded] = useState(false);
  const [cardsGroupExpanded, setCardsGroupExpanded] = useState(false);
  const [cardAccounts, setCardAccounts] = useState([]);
  const [cardsSyncing, setCardsSyncing] = useState(false);
  const [cardsSyncResult, setCardsSyncResult] = useState(null);
  const [selectedCardFilter, setSelectedCardFilter] = useState('all');
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);

  useEffect(() => {
    const reload = () => fetchAndFilterData();
    window.addEventListener('accounting:transactions-changed', reload);
    return () => window.removeEventListener('accounting:transactions-changed', reload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount, selectedMonth, selectedCategory]);

  const fetchAndFilterData = async () => {
    setLoading(true);
    setError('');
    try {
      const [accountsRes, transactionsRes, categoriesRes, accountsSummaryRes] = await Promise.all([
        getAccounts(true), // Include archived accounts for transaction history
        getTransactions(selectedAccount, selectedMonth, selectedCategory),
        getCategories(true), // Include deleted categories for transaction history
        getAccountsSummary()
      ]);

      // Separate active and archived accounts
      const allAccounts = accountsRes.data;
      const activeAccounts = allAccounts.filter(a => a.account_status !== 'archived');
      const archived = allAccounts.filter(a => a.account_status === 'archived');
      
      const creditAccounts = activeAccounts.filter(a => a.account_type === 'credit');

      setAccounts(allAccounts); // Keep all for transaction display
      setArchivedAccounts(archived);
      setCardAccounts(creditAccounts);
      setTransactions(transactionsRes.data);
      setCategories(categoriesRes.data);
      setAccountsSummary(accountsSummaryRes.data);

      const filtered = transactionsRes.data.filter(t => {
        let accountMatch;
        if (!selectedAccount) {
          // Default: show only active accounts
          const account = allAccounts.find(a => a.id === t.account_id);
          accountMatch = account && account.account_status !== 'archived';
        } else if (selectedAccount === 'all') {
          // Show all accounts including archived
          accountMatch = true;
        } else {
          // Show specific account
          accountMatch = t.account_id === selectedAccount;
        }
        const categoryMatch = !selectedCategory || t.category_id === selectedCategory;
        return accountMatch && categoryMatch;
      });
      setFilteredTransactions(filtered);

    } catch (err) {
      console.error("Failed to fetch data", err);
      setError(err.response?.data?.msg || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchDateRange = async () => {
        try {
            const res = await getTransactionDateRange();
            if (res.data.earliest_date) {
                const earliestDateStr = res.data.earliest_date;
                const year = parseInt(earliestDateStr.substring(0, 4), 10);
                const month = parseInt(earliestDateStr.substring(5, 7), 10) - 1;
                const earliestLoopDate = new Date(year, month, 1);

                const options = [];
                
                const today = new Date();
                const currentYear = today.getFullYear();
                const currentMonth = today.getMonth();
                let loopDate = new Date(currentYear, currentMonth, 1);

                while (loopDate >= earliestLoopDate) {
                    const monthStr = loopDate.toLocaleString('default', { month: 'long' });
                    const yearStr = loopDate.getFullYear();
                    const monthValue = loopDate.getMonth() + 1;
                    options.push({ value: `${yearStr}-${monthValue < 10 ? '0' + monthValue : monthValue}`, label: `${monthStr} ${yearStr}` });
                    
                    loopDate.setMonth(loopDate.getMonth() - 1);
                }
                setMonthOptions(options);
            }
        } catch (err) {
            console.error("Failed to fetch date range", err);
        }
    };

    fetchDateRange();
    fetchAndFilterData();
  }, [selectedAccount, selectedMonth, selectedCategory]);

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  const handleOpenAccountModal = () => setIsAccountModalOpen(true);
  const handleCloseAccountModal = () => setIsAccountModalOpen(false);

  const handleTransactionAdded = () => {
    fetchAndFilterData();
  };

  const handleAccountAdded = () => {
    fetchAndFilterData();
  };

  const handleReactivateAccount = async (accountId) => {
    try {
      await reactivateAccount(accountId);
      fetchAndFilterData(); // Refresh all data
      alert('Account reactivated successfully!');
    } catch (err) {
      console.error('Failed to reactivate account', err);
      alert('Failed to reactivate account: ' + (err.response?.data?.msg || 'Unknown error'));
    }
  };

  const handleEditTransaction = (transaction) => {
    setSelectedTransaction(transaction);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedTransaction(null);
  };

  const handleTransactionUpdated = (message) => {
    if (typeof message === 'string') setSuccessNotice(message);
    fetchAndFilterData();
  };

  const handleDeleteTransaction = (id) => {
    setTransactionToDelete(id);
    setOpenConfirmDelete(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteTransaction(transactionToDelete);
      fetchAndFilterData();
      setOpenConfirmDelete(false);
      setTransactionToDelete(null);
    } catch (err) {
      console.error("Failed to delete transaction", err);
      setError(err.response?.data?.msg || 'Failed to delete transaction');
    }
  };

  const handleCancelDelete = () => {
    setOpenConfirmDelete(false);
    setTransactionToDelete(null);
  };

  const handleSyncCards = async () => {
    setCardsSyncing(true);
    setCardsSyncResult(null);
    try {
      const res = await triggerTellerSync();
      setCardsSyncResult(res.data);
      fetchAndFilterData();
    } catch (err) {
      console.error('Teller sync failed', err);
      setCardsSyncResult({ error: err.response?.data?.msg || 'Sync failed' });
    } finally {
      setCardsSyncing(false);
    }
  };

  const MobileFilterAccordion = () => (
    <Accordion 
      expanded={mobileFilterExpanded} 
      onChange={(e, expanded) => setMobileFilterExpanded(expanded)}
      sx={{ mb: 2 }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography>
          Filters {selectedAccount || selectedCategory || selectedMonth !== 'last_30_days' ? '(Active)' : ''}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          <FormControl fullWidth>
            <InputLabel>Account</InputLabel>
            <SearchableSelect
              value={selectedAccount}
              onChange={(e) => setSelectedAccount(e.target.value)}
              label="Account"
            >
              <MenuItem value="">All Active Accounts</MenuItem>
              <MenuItem value="all">All Accounts (including archived)</MenuItem>
              {accounts.filter(a => a.account_status !== 'archived' || a.id === selectedAccount).map((account) => (
                <MenuItem key={account.id} value={account.id}>
                  {account.name}{account.account_status === 'archived' ? ' (Archived)' : ''}
                </MenuItem>
              ))}
            </SearchableSelect>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <SearchableSelect
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              label="Category"
            >
              <MenuItem value="">All Categories</MenuItem>
              {categories.filter(c => (!selectedAccount || c.account_id === selectedAccount) && !c.is_deleted).map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </SearchableSelect>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Month</InputLabel>
            <Select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              label="Month"
            >
              <MenuItem value="last_30_days">Last 30 Days</MenuItem>
              <MenuItem value="full_history">Full History</MenuItem>
              {monthOptions.map((month) => (
                <MenuItem key={month.value} value={month.value}>
                  {month.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button 
            variant="outlined" 
            onClick={() => {
              setSelectedAccount('');
              setSelectedCategory('');
              setSelectedMonth('last_30_days');
              setMobileFilterExpanded(false);
            }}
            fullWidth
            sx={{ mt: 1 }}
          >
            Clear Filters
          </Button>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  const creditAccountIds = new Set(cardAccounts.map(a => a.id));
  const depositoryTransactions = filteredTransactions.filter(t => !creditAccountIds.has(t.account_id));
  const cardTransactions = transactions.filter(t => creditAccountIds.has(t.account_id));

  return (
    <Container sx={{ mt: { xs: 2, md: 4 }, px: { xs: 1, sm: 2, md: 3 } }}>
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Dashboard
        </Typography>
        <ComplianceDashboardAlert />
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {successNotice && <Alert severity="success" onClose={() => setSuccessNotice('')} sx={{ mb: 2 }}>{successNotice}</Alert>}

        <Box sx={{ mb: 4, display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Button
            variant="contained"
            onClick={handleOpenModal}
            sx={{ py: { xs: 1.5, md: 1 }, fontSize: { xs: '1rem', md: '0.875rem' } }}
            fullWidth={isMobile}
          >
            Add Transaction
          </Button>
          <Button
            variant="outlined"
            onClick={() => setIsTransferModalOpen(true)}
            sx={{ py: { xs: 1.5, md: 1 }, fontSize: { xs: '1rem', md: '0.875rem' } }}
            fullWidth={isMobile}
          >
            Transfer
          </Button>
          <Button
            variant="outlined"
            onClick={handleOpenAccountModal}
            sx={{ py: { xs: 1.5, md: 1 }, fontSize: { xs: '1rem', md: '0.875rem' } }}
            fullWidth={isMobile}
          >
            Add Account
          </Button>
        </Box>

        {/* Tab Switcher */}
        {cardAccounts.length > 0 && (
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="fullWidth"
            sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Accounts" />
            <Tab label="Cards" icon={<CreditCardIcon />} iconPosition="start" />
          </Tabs>
        )}

        {/* ===== TAB 0: ACCOUNTS ===== */}
        {activeTab === 0 && (
          <>
            {isMobile ? (
              <MobileFilterAccordion />
            ) : (
              <Box sx={{ mb: 4, display: 'flex', gap: 2 }}>
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>Account</InputLabel>
                  <SearchableSelect
                    value={selectedAccount}
                    onChange={(e) => setSelectedAccount(e.target.value)}
                    label="Account"
                  >
                    <MenuItem value="">All Active Accounts</MenuItem>
                    <MenuItem value="all">All Accounts (including archived)</MenuItem>
                    {accounts.filter(a => a.account_status !== 'archived' || a.id === selectedAccount).map((account) => (
                      <MenuItem key={account.id} value={account.id}>
                        {account.name}{account.account_status === 'archived' ? ' (Archived)' : ''}
                      </MenuItem>
                    ))}
                  </SearchableSelect>
                </FormControl>
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>Category</InputLabel>
                  <SearchableSelect
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    label="Category"
                  >
                    <MenuItem value="">All Categories</MenuItem>
                    {categories.filter(c => (!selectedAccount || c.account_id === selectedAccount) && !c.is_deleted).map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </SearchableSelect>
                </FormControl>
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>Month</InputLabel>
                  <Select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    label="Month"
                  >
                    <MenuItem value="last_30_days">Last 30 Days</MenuItem>
                    <MenuItem value="full_history">Full History</MenuItem>
                    {monthOptions.map((month) => (
                      <MenuItem key={month.value} value={month.value}>
                        {month.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            <Accordion expanded={accountsGroupExpanded} onChange={(e, expanded) => setAccountsGroupExpanded(expanded)} sx={{ mb: 4 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="accounts-panel-content"
                id="accounts-panel-header"
              >
                <Typography variant="h6">Accounts</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                {/* Account Migration Tool Button */}
                <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', backgroundColor: '#f8f9fa' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setIsMigrationModalOpen(true)}
                    sx={{ mb: 1 }}
                  >
                    🔄 Account Migration Tool
                  </Button>
                  <Typography variant="caption" display="block" color="text.secondary">
                    Transfer balances between accounts
                  </Typography>
                </Box>

                <Grid container spacing={{ xs: 2, md: 3 }} sx={{ p: 2 }}>
                  {accounts.filter(a => a.account_status !== 'archived' && a.account_type !== 'credit').map((account) => (
                    <Grid item xs={12} sm={6} md={4} key={account.id}>
                      <Accordion defaultExpanded={false} sx={{ width: '100%' }}>
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          aria-controls={`panel-${account.id}-content`}
                          id={`panel-${account.id}-header`}
                        >
                          <Typography variant="h6">{account.name}</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 0 }}>
                          <AccountCard
                            account={account}
                            transactions={transactions}
                            categories={categories}
                            sx={{
                              height: '100%',
                              minHeight: { xs: 'auto', md: 200 },
                              boxShadow: 'none',
                              border: 'none',
                            }}
                          />
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Archived Accounts Section */}
            {archivedAccounts.length > 0 && (
              <Accordion
                expanded={showArchivedAccounts}
                onChange={(e, expanded) => setShowArchivedAccounts(expanded)}
                sx={{ mt: 2, mb: 2, backgroundColor: '#f5f5f5' }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="archived-accounts-panel-content"
                  id="archived-accounts-panel-header"
                >
                  <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                    📁 Archived Accounts ({archivedAccounts.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={{ xs: 2, md: 3 }}>
                    {archivedAccounts.map((account) => (
                      <Grid item xs={12} sm={6} md={4} key={account.id}>
                        <Paper
                          sx={{
                            p: 2,
                            opacity: 0.7,
                            backgroundColor: '#fafafa',
                            border: '1px solid #e0e0e0'
                          }}
                        >
                          <Typography variant="h6" gutterBottom>
                            {account.name} (Archived)
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Archived: {account.archived_date ? new Date(account.archived_date).toLocaleDateString() : 'N/A'}
                          </Typography>
                          <Typography variant="body2" gutterBottom>
                            Reason: {account.archive_reason || 'No reason specified'}
                          </Typography>
                          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleReactivateAccount(account.id)}
                            >
                              Reactivate
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => {
                                setSelectedAccount(account.id);
                                setSelectedMonth('all');
                                fetchAndFilterData();
                              }}
                            >
                              View History
                            </Button>
                          </Box>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            )}

            <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mt: { xs: 2, md: 4 } }}>
              <Grid item xs={12} md={6}>
                <Accordion defaultExpanded={false}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="account-totals-panel-content"
                    id="account-totals-panel-header"
                  >
                    <Typography variant="h6">Account Totals Summary</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Paper sx={{ p: { xs: 2, md: 3 }, boxShadow: 'none' }}>
                      <List>
                        {accountsSummary.map((summary) => (
                          <ListItem key={summary.account_id} divider>
                            <ListItemText
                              primary={summary.account_name}
                              secondary={`${summary.account_total_balance}`}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  </AccordionDetails>
                </Accordion>
              </Grid>
              <Grid item xs={12} md={6} sx={{ mt: { xs: 2, md: 0 } }}>
                <Paper sx={{ p: { xs: 2, md: 3 } }}>
                  <Typography variant="h6" gutterBottom>Recent Transactions</Typography>
                  <TransactionTable
                    transactions={depositoryTransactions}
                    accounts={accounts}
                    categories={categories}
                    onEdit={handleEditTransaction}
                    onDelete={handleDeleteTransaction}
                    onTransactionsUpdated={handleTransactionUpdated}
                  />
                </Paper>
              </Grid>
            </Grid>
          </>
        )}

        {/* ===== TAB 1: CARDS ===== */}
        {activeTab === 1 && (
          <>
            <Accordion
              expanded={cardsGroupExpanded}
              onChange={(e, expanded) => setCardsGroupExpanded(expanded)}
              sx={{ mb: 4 }}
              defaultExpanded
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="cards-panel-content"
                id="cards-panel-header"
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <CreditCardIcon color="primary" />
                  <Typography variant="h6">Cards</Typography>
                  <Chip
                    label={cardAccounts.length}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ ml: 1 }}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', backgroundColor: '#f8f9fa', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={cardsSyncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
                    onClick={handleSyncCards}
                    disabled={cardsSyncing}
                    size="small"
                  >
                    {cardsSyncing ? 'Syncing...' : 'Sync All Cards'}
                  </Button>
                  {cardsSyncResult && !cardsSyncResult.error && (
                    <Typography variant="body2" color="success.main">
                      {cardsSyncResult.total_new} new, {cardsSyncResult.total_updated} updated
                    </Typography>
                  )}
                  {cardsSyncResult?.error && (
                    <Typography variant="body2" color="error.main">
                      {cardsSyncResult.error}
                    </Typography>
                  )}
                </Box>

                <Grid container spacing={{ xs: 2, md: 3 }} sx={{ p: 2 }}>
                  {cardAccounts.map((account) => (
                    <Grid item xs={12} sm={6} md={4} key={account.id}>
                      <Accordion defaultExpanded={false} sx={{ width: '100%' }}>
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          aria-controls={`card-panel-${account.id}-content`}
                          id={`card-panel-${account.id}-header`}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CreditCardIcon fontSize="small" color="action" />
                            <Typography variant="h6">{account.name}</Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 0 }}>
                          <AccountCard
                            account={account}
                            transactions={transactions}
                            categories={categories}
                            sx={{
                              height: '100%',
                              minHeight: { xs: 'auto', md: 200 },
                              boxShadow: 'none',
                              border: 'none',
                            }}
                          />
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Card Transactions */}
            <Paper sx={{ p: { xs: 2, md: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h6">Card Transactions</Typography>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Filter by card</InputLabel>
                  <SearchableSelect
                    value={selectedCardFilter}
                    onChange={(e) => setSelectedCardFilter(e.target.value)}
                    label="Filter by card"
                  >
                    <MenuItem value="all">All Cards</MenuItem>
                    {cardAccounts.map((ca) => (
                      <MenuItem key={ca.id} value={ca.id}>{ca.name}</MenuItem>
                    ))}
                  </SearchableSelect>
                </FormControl>
              </Box>
              <TransactionTable
                transactions={selectedCardFilter === 'all' ? cardTransactions : cardTransactions.filter(t => t.account_id === selectedCardFilter)}
                accounts={accounts}
                categories={categories}
                onEdit={handleEditTransaction}
                onDelete={handleDeleteTransaction}
                onTransactionsUpdated={handleTransactionUpdated}
              />
            </Paper>
          </>
        )}

        <AddTransactionModal
          open={isModalOpen}
          handleClose={handleCloseModal}
          onTransactionAdded={handleTransactionAdded}
        />

        <TransferModal
          open={isTransferModalOpen}
          onClose={() => setIsTransferModalOpen(false)}
          accounts={accounts}
          categories={categories}
          onTransferComplete={handleTransactionAdded}
        />

        <AddAccountModal
          open={isAccountModalOpen}
          handleClose={handleCloseAccountModal}
          onAccountAdded={handleAccountAdded}
        />

        {selectedTransaction && (
          <EditTransactionModal
            open={isEditModalOpen}
            handleClose={handleCloseEditModal}
            transaction={selectedTransaction}
            onTransactionUpdated={handleTransactionUpdated}
          />
        )}

        <Dialog
          open={openConfirmDelete}
          onClose={handleCancelDelete}
        >
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this transaction? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDelete}>Cancel</Button>
            <Button onClick={handleConfirmDelete} color="error">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        <AccountMigrationModal
          open={isMigrationModalOpen}
          onClose={() => setIsMigrationModalOpen(false)}
          accounts={accounts}
          onMigrationComplete={fetchAndFilterData}
        />
      </Box>
    </Container>
  );
}

export default DashboardPage;
